import {
	Component,
	forwardRef,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { NG_VALIDATORS, NgModel } from '@angular/forms';

import { InsightSearchHelper } from 'rev-portal/search/InsightSearchHelper.Service';
import { ISearchLoader } from 'rev-portal/search/Contract';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { toggleError } from 'rev-shared/util/directives/form/validation/validationUtil';
import { uniq } from 'rev-shared/util';

import { EditWebcastBaseSettingsComponent } from './EditWebcastBaseSettings.Component';
import { EditWebcastModel } from './EditWebcastModel';
import { EditWebcastService } from './EditWebcast.Service';
import { WebcastVideoSource } from '../webcast/WebcastVideoSource';

import styles from './EditWebcast.module.less';

@Component({
	selector: 'edit-webcast-hosts-settings',
	templateUrl: './EditWebcastHostsSettings.Component.html',
	providers: [{
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => EditWebcastHostsSettingsComponent),
		multi: true,
	}],
	standalone: false
})
export class EditWebcastHostsSettingsComponent extends EditWebcastBaseSettingsComponent implements OnInit, OnChanges {
	@Input() public webcast: EditWebcastModel;
	@Input() public features: IMediaFeatures;
	@Input() public isSidebar: boolean;
	@Input() public hidden: boolean;

	public maxAllowedEventHosts: number;
	public maxAllowedCombinedPresentersAndHosts: number = 9;
	public readonly accountId: string = this.UserContext.getAccount().id;
	public readonly WebcastVideoSource = WebcastVideoSource;
	public readonly styles = styles;

	public eventAdminInsightOptions: any;
	public presenterInsightOptions: any;
	public moderatorInsightOptions: any;
	public userSearchLoader: ISearchLoader;

	@ViewChild('eventAdmins') public hostsInsight: NgModel;
	@ViewChild('producerPresenter') public presentersInsight: NgModel;
	@ViewChild('externalPresenters') public externalPresenters: NgModel;

	constructor(
		private readonly EditWebcastService: EditWebcastService,
		private readonly InsightSearchHelper: InsightSearchHelper,
		private readonly UserContext: UserContextService
	) {
		super();
	}

	public ngOnInit(): void {
		if(this.webcast.presenterId) {
			this.InsightSearchHelper.getAccessEntities(this.UserContext.getAccount().id, [this.webcast.presenterId])
				.then(results => {
					this.webcast.presenter = results.accessEntities[0];
				});
		}
		if(this.webcast.presenterIds) {
			this.InsightSearchHelper.getAccessEntities(this.UserContext.getAccount().id, this.webcast.presenterIds)
				.then(results => {
					this.webcast.presenters = results.accessEntities;
					this.validateMaxPresenters();
				});
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if(this.webcast) {
			this.initInsightOptions();
			this.maxAllowedEventHosts = this.getMaxHostNumber();
		}
	}

	public initInsightOptions(): void {
		if(this.eventAdminInsightOptions) {
			return;
		}

		const { accountId, webcast } = this;

		this.eventAdminInsightOptions = this.InsightSearchHelper.buildAccessEntityInsightOptions({
			accountId,
			users: true,
			loadQueryPage: query => this.EditWebcastService.queryEventAdmins(query),
			getEntityIds: () => webcast.eventAdmins.map(e => e.id)
		});

		this.presenterInsightOptions = this.InsightSearchHelper.buildAccessEntityInsightOptions({
			accountId,
			users: true,
			getEntityIds: () => webcast.presenters.map(e => e.id)
		});

		this.moderatorInsightOptions = this.InsightSearchHelper.buildAccessEntityInsightOptions({
			accountId,
			users: true,
			getEntityIds: () => webcast.moderators.map(e => e.id)
		});

		Object.defineProperty(this.moderatorInsightOptions, 'moderatorWithSlideControl', {
			//hack to get a callback when slide user changes
			get: () => this.webcast.userIdSlideControl,
			set: (val: string) => this.webcast.userIdSlideControl = val
		});

		this.userSearchLoader = this.InsightSearchHelper.getUserSearchLoader(this.accountId, []);
	}

	public validateMaxPresenters(): void {
		const hosts = this.hostsInsight?.control;
		const presenters = this.presentersInsight?.control;
		const externalPresenters = this.externalPresenters?.control;

		const presenterIds = uniq([
			...this.webcast.eventAdmins,
			...this.webcast.presenters
		]
			.map(u => u.id));

		const numPresenters = presenterIds.length +
			(this.features.enableExternalPresenters ? this.webcast.externalPresenters.length : 0);

		const isOverMaxPresenters = numPresenters > this.maxAllowedCombinedPresentersAndHosts;
		hosts && toggleError(hosts, 'maxPresenters', isOverMaxPresenters);
		presenters && toggleError(presenters, 'maxPresenters', isOverMaxPresenters);
		externalPresenters && toggleError(externalPresenters, 'maxPresenters', isOverMaxPresenters);
	}

	public getMaxHostNumber(): number {
		return this.features?.enableTrustedPublicWebcastAccess ? Infinity : 3;
	}
}
